import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  FileText, 
  Eye,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Package
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminQuotes = () => {
  const { authFetch } = useAdmin();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchQuotes();
  }, [page, statusFilter]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/quotes?skip=${page * limit}&limit=${limit}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const response = await authFetch(url);
      const data = await response.json();
      setQuotes(data.quotes);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewQuote = async (quote) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/quotes/${quote.quote_id}`);
      const data = await response.json();
      setSelectedQuote(data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching quote:', error);
    }
  };

  const updateStatus = async (quoteId, newStatus) => {
    try {
      await authFetch(`${API_URL}/api/admin/quotes/${quoteId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      
      fetchQuotes();
      if (selectedQuote) {
        setSelectedQuote({ ...selectedQuote, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'reviewed': return <Eye className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quote Requests</h1>
            <p className="text-gray-600">{total} total quotes</p>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Quotes Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
              </div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No quotes found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="quotes-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quote ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {quotes.map((quote) => (
                      <tr key={quote.quote_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-mono text-gray-600">
                          {quote.quote_id.slice(0, 8)}...
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{quote.company_name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">{quote.contact_name}</p>
                          <p className="text-sm text-gray-500">{quote.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-sm rounded">
                            {quote.items_count} items
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(quote.status)}`}>
                            {getStatusIcon(quote.status)}
                            {quote.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(quote.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => viewQuote(quote)}
                            data-testid={`view-quote-${quote.quote_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quote Detail Modal */}
      {showModal && selectedQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Quote Details</CardTitle>
                <p className="text-sm text-gray-500 font-mono">{selectedQuote.quote_id}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Current Status</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full mt-1 ${getStatusColor(selectedQuote.status)}`}>
                    {getStatusIcon(selectedQuote.status)}
                    {selectedQuote.status}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => updateStatus(selectedQuote.quote_id, 'reviewed')}
                    disabled={selectedQuote.status === 'reviewed'}
                  >
                    Mark Reviewed
                  </Button>
                  <Button 
                    size="sm" 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => updateStatus(selectedQuote.quote_id, 'completed')}
                    disabled={selectedQuote.status === 'completed'}
                  >
                    Mark Completed
                  </Button>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Company</h3>
                  <p className="text-gray-700">{selectedQuote.company_name}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Contact</h3>
                  <p className="text-gray-700">{selectedQuote.contact_name}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <a href={`mailto:${selectedQuote.email}`} className="text-pink-600 hover:underline">
                    {selectedQuote.email}
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                  <a href={`tel:${selectedQuote.phone}`} className="text-pink-600 hover:underline">
                    {selectedQuote.phone}
                  </a>
                </div>
              </div>

              {/* Delivery Address */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Delivery Address</h3>
                <p className="text-gray-700">{selectedQuote.delivery_address}</p>
              </div>

              {/* Decoration Method */}
              {selectedQuote.decoration_method && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Decoration Method</h3>
                  <p className="text-gray-700">{selectedQuote.decoration_method}</p>
                </div>
              )}

              {/* Additional Notes */}
              {selectedQuote.additional_notes && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Additional Notes</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedQuote.additional_notes}</p>
                </div>
              )}

              {/* Items */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Requested Items ({selectedQuote.items?.length || 0})</h3>
                <div className="space-y-3">
                  {selectedQuote.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center overflow-hidden border">
                        {item.image_url ? (
                          <img 
                            src={item.image_url.startsWith('/') ? `https://procloudpromo.com${item.image_url}` : item.image_url}
                            alt={item.product_name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Package className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.product_name}</p>
                        <p className="text-sm text-gray-500">Code: {item.product_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{item.quantity} units</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Date */}
              <div className="text-sm text-gray-500 pt-4 border-t">
                Submitted: {new Date(selectedQuote.created_at).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminQuotes;
